import { Head, router } from "@inertiajs/react"
import { Check, ChevronDown, ChevronRight, Pin } from "lucide-react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Log {
  id: number
  log_type: string
  log_date: string | null
  title: string | null
  content_preview: string | null
  user_name: string | null
  urgent: boolean
  sensitive: boolean
  significant: boolean
  pinned: boolean
  case_id: number
}

interface CaseGroup {
  case_id: number
  case_number: string
  case_name: string
  logs: Log[]
}

interface Props {
  grouped_logs: CaseGroup[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: "/dashboard" },
  { title: "Reviews", href: "/case_log_reviews" },
]

const typeBadge = (type: string) => {
  const styles: Record<string, string> = {
    update: "bg-hpi-light-blue text-hpi-dark-blue",
    todo: "bg-hpi-yellow/30 text-hpi-dark-gray",
    restricted: "bg-hpi-red/20 text-hpi-red",
    accounting: "bg-hpi-dark-green/20 text-hpi-dark-green",
  }
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs ${styles[type] || "bg-hpi-pale-gray text-hpi-dark-gray"}`}
    >
      {type}
    </span>
  )
}

function CaseSection({ group }: { group: CaseGroup }) {
  const [expanded, setExpanded] = useState(true)

  const handleMarkRead = (logId: number) => {
    router.post("/case_log_reviews", {
      case_log_id: logId,
      read_type: "read",
    })
  }

  const handleTogglePin = (logId: number, currentlyPinned: boolean) => {
    if (currentlyPinned) {
      router.delete(`/case_log_reviews/${logId}`)
    } else {
      router.post("/case_log_reviews", {
        case_log_id: logId,
        read_type: "pinned",
      })
    }
  }

  const handleMarkAllRead = () => {
    if (group.logs.length === 0) return
    router.post("/case_log_reviews", {
      case_log_id: group.logs[0].id,
      case_id: group.case_id,
      mark_all: true,
    })
  }

  return (
    <div className="border-hpi-light-gray mb-4 rounded border bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="hover:bg-hpi-pale-blue flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="text-hpi-med-gray h-4 w-4" />
          ) : (
            <ChevronRight className="text-hpi-med-gray h-4 w-4" />
          )}
          <span className="font-semibold">{group.case_name}</span>
          <span className="bg-hpi-pale-gray text-hpi-med-blue rounded-full px-2 py-0.5 text-xs">
            {group.logs.length}
          </span>
        </div>
        {expanded && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleMarkAllRead()
            }}
            className="border-hpi-light-gray text-hpi-med-blue hover:bg-hpi-pale-gray rounded border px-3 py-1 text-xs"
          >
            Mark All as Read
          </button>
        )}
      </button>

      {expanded && (
        <div className="border-hpi-light-gray border-t">
          {group.logs.map((log) => (
            <div
              key={log.id}
              className="border-hpi-light-gray flex items-center justify-between border-b px-4 py-3 last:border-b-0"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {typeBadge(log.log_type)}
                  <span className="text-hpi-med-gray text-sm">
                    {formatDate(log.log_date)}
                  </span>
                  <span className="text-hpi-med-gray text-sm">
                    {log.user_name}
                  </span>
                  {log.urgent && (
                    <span className="bg-hpi-red/20 text-hpi-red rounded px-2 py-0.5 text-xs">
                      Urgent
                    </span>
                  )}
                  {log.sensitive && (
                    <span className="rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700">
                      Sensitive
                    </span>
                  )}
                  {log.significant && (
                    <span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-700">
                      Significant
                    </span>
                  )}
                  {log.pinned && (
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
                      Pinned
                    </span>
                  )}
                </div>
                <a
                  href={`/cases/${log.case_id}/logs/${log.id}`}
                  className="text-hpi-dark-gray hover:text-hpi-active-blue mt-1 block text-sm"
                >
                  {log.title || log.content_preview || "(No content)"}
                </a>
              </div>
              <div className="ml-4 flex gap-1">
                <button
                  onClick={() => handleTogglePin(log.id, log.pinned)}
                  className={`rounded p-1.5 ${
                    log.pinned
                      ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                      : "text-hpi-med-gray hover:bg-hpi-pale-gray hover:text-hpi-med-blue"
                  }`}
                  title={log.pinned ? "Unpin" : "Pin"}
                >
                  <Pin className="h-4 w-4" />
                </button>
                {!log.pinned && (
                  <button
                    onClick={() => handleMarkRead(log.id)}
                    className="text-hpi-med-gray hover:bg-hpi-dark-green/20 hover:text-hpi-dark-green rounded p-1.5"
                    title="Mark as Read"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Index({ grouped_logs }: Props) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Reviews" />

      <div className="p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">Case Log Reviews</h1>
          <p className="text-hpi-med-gray text-sm">
            Unread and pinned case logs from the last 14 days
          </p>
        </div>

        {grouped_logs.length > 0 ? (
          grouped_logs.map((group) => (
            <CaseSection key={group.case_id} group={group} />
          ))
        ) : (
          <div className="border-hpi-light-gray text-hpi-med-gray rounded border bg-white p-8 text-center">
            All caught up! No unread case logs.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
